import { ReactNode, useMemo } from 'react';
import { FilmStripIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { ChipPopover, Tag } from '@/components';
import { useSourcesStore } from '@/stores';
import { SourceType, getSelectableSourceType } from '@/types/source';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { I18nNamespace } from '@/lib/i18n-namespaces';

export interface ControlledSourcesPopover {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    content: ReactNode;
}

export interface ControlledSourcesSelection {
    selected: Set<string>;
    excluded: Set<string>;
    all: boolean;
}

interface ControlledSourcesChipProps {
    popover: ControlledSourcesPopover;
    selection: ControlledSourcesSelection;
    emptyLabel?: string;
    invalidHighlight?: boolean;
    tagVariant?: 'base' | 'plain';
}

const INVALID_CHIP_CLASSES = 'bg-grey-500 border-grey-400 text-white-100';

export function ControlledSourcesChip({
    popover,
    selection,
    emptyLabel,
    invalidHighlight = false,
    tagVariant = 'base',
}: ControlledSourcesChipProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const sourcesMap = useSourcesStore((state) => state.sourcesMap);

    const { selected, excluded, all } = selection;

    const selectedSourceType = useMemo((): SourceType | undefined => {
        let foundType: SourceType | undefined;
        for (const id of Array.from(selected)) {
            const source = sourcesMap.get(id);
            if (!source) continue;
            const type = getSelectableSourceType(source.type);
            if (foundType && type !== foundType) return undefined;
            foundType = type;
        }
        return foundType;
    }, [selected, sourcesMap]);

    const label = useMemo(
        () =>
            computeLabel({
                selected,
                excluded,
                all,
                emptyLabel,
                selectedSourceType,
                getSourceName: (id) => {
                    const source = sourcesMap.get(id);
                    return source?.displayName ?? source?.name;
                },
                t,
            }),
        [
            selected,
            excluded,
            all,
            emptyLabel,
            selectedSourceType,
            sourcesMap,
            t,
        ],
    );

    const chipIcon =
        selectedSourceType === SourceType.FILE ? (
            <FilmStripIcon weight="fill" />
        ) : (
            <SecurityCameraIconMirrored weight="fill" />
        );

    const togglePopover = () => popover.onOpenChange(!popover.open);

    return (
        <ChipPopover
            popoverOpen={popover.open}
            onPopoverOpenChange={popover.onOpenChange}
            chip={
                <Tag
                    data-testid="filter-chip-sources"
                    variant={tagVariant}
                    size="sm"
                    leadingIcon={chipIcon}
                    onClick={togglePopover}
                    className={
                        invalidHighlight ? INVALID_CHIP_CLASSES : undefined
                    }
                >
                    {label}
                </Tag>
            }
        >
            {popover.content}
        </ChipPopover>
    );
}

interface ComputeLabelArgs {
    selected: Set<string>;
    excluded: Set<string>;
    all: boolean;
    emptyLabel?: string;
    selectedSourceType?: SourceType;
    getSourceName: (id: string) => string | undefined;
    t: (key: string, options?: Record<string, unknown>) => string;
}

const MULTI_SOURCE_LABEL_KEY: Record<SourceType, string> = {
    [SourceType.FILE]: 'filters.selectedFiles',
    [SourceType.CAMERA]: 'filters.selectedCameras',
    [SourceType.FLIR]: 'filters.selectedCameras',
};

function computeLabel({
    selected,
    excluded,
    all,
    emptyLabel,
    selectedSourceType,
    getSourceName,
    t,
}: ComputeLabelArgs): string {
    if (selected.size === 0 && excluded.size === 0) {
        return emptyLabel ?? '';
    }

    if (all && excluded.size > 0) {
        return t('filters.allSourcesExcept', { count: excluded.size });
    }

    if (selected.size === 1) {
        return getSourceName(Array.from(selected)[0]) ?? '';
    }

    if (selected.size > 1) {
        const key = selectedSourceType
            ? MULTI_SOURCE_LABEL_KEY[selectedSourceType]
            : 'filters.selectedSources';
        return t(key, { count: selected.size });
    }

    return '';
}
